/**
 * Zoom API plan-type -> human "Rate Plan Name", from the June 1 2026 Multi-Currency Global Channel
 * Price List (sheet "Monthly Multi-Currency Prices": col E "API Plan Type" -> col C "Rate Plan Name").
 * Raw API types (e.g. "zoe_yearly", "roomconnector_yearly") are opaque to a CSM; planName() maps them
 * to the real product name, deriving period variants (free trial / monthly / multi-year) from the base.
 */

const PLAN_NAMES: Record<string, string> = {
  // Add-on plan types missing from the price-list import (added 2026-06-26 after a coverage audit).
  // "zwr" = Workspace Reservation; the *_yearly anchor also lets planName derive the period variants.
  zwr_yearly: "Zoom Workspace Reservation Annual",
  zwr_three_year: "Zoom Workspace Reservation Three Year Prepay",
  phone_free_trial: "Zoom Phone (Free Trial)",
  // Events-family trials (A6, wave 4): ZPCD sku_name_map's rate_plan_name with the trial term kept in
  // planName's own "(Free Trial)" casing. The corpus confirms the product part of each (Finnomena "Zoom
  // Events"; DOA / Yuanta "Zoom Events Unlimited"; Yuanta "Zoom Webinars Plus Unlimited", all "500
  // attendees"). Before wave 4 these read "Zoom Events Plus 500 …" / "Zoom Events 500 …" — names Zoom
  // does not use.
  eventplus500_freetrial: "Zoom Events (Free Trial)", // sku_name_map "Zoom Events (free trial)"
  ze_500_freetrial: "Zoom Events Unlimited — 500 attendees (Free Trial)", // sku_name_map, same words
  zec_500_freetrial: "Zoom Webinars Plus Unlimited — 500 attendees (Free Trial)", // sku_name_map, same words
  // Zoom Phone calling-licence TRIAL lines (N0-2, 2026-09-18). Not in the price list, so they fell to the
  // title-cased fallback ("Phone Calling Pro (Free Trial)", 54 + 3 accounts on prod). The names are what
  // Zoom's own console prints for the line (API repo docs/ZOOM-DISPLAY-CORPUS.md: NTT Data's
  // pro_free_trial line; LDC Dental's child line for Global Select; the owner reported it on Nova
  // Dynamics). The term is NOT in the name — billingPeriod still reads it from the
  // "_free_trial" suffix.
  phone_calling_pro_free_trial: "Zoom Phone Pro",
  phone_calling_pro_global_free_trial: "Zoom Phone Pro Global Select",
  // W4-Q8 (owner ruling 2026-09-19): ZPCD sku_name_map's rate_plan_name, verbatim (source 'manual',
  // "term sibling of phone_calling_pro_one_year = Zoom Phone Pro Annual"; 2 local lines). Was the
  // title-cased fallback "Phone Calling Pro (Monthly)". The term stays in the name here, as on every
  // surface without a Billing Period column (W4-Q1); the Subscription tab prints "Zoom Phone Pro" +
  // Billing Period "Monthly" (lib/zoomLine.ts). No corpus screenshot of this SKU exists.
  phone_calling_pro_monthly: "Zoom Phone Pro Monthly",
  // A6 (wave 4, 2026-09-19) — raw codes that used to fall to the title-cased fallback ("Trial",
  // "Monthly", "PLANCODE 1514", "Zkw (Free Trial)", "Pro (Free Trial)", "Zoent (Free Trial)",
  // "Cmr (Free Trial)", "Zccnb (Free Trial)"). Each name is what Zoom's console prints for the line
  // (API repo docs/ZOOM-DISPLAY-CORPUS.md) or what ZPCD's sku_name_map records, plus the term and
  // capacity this function keeps for surfaces with no Billing Period / Details column. The
  // Subscription tab prints the bare product instead (lib/zoomLine.ts productName, T3).
  trial: "Free Trial", // corpus Round 4 (STATE RAILWAY, Suan Sunandha)
  monthly: "Zoom Workplace Pro Monthly", // sku_name_map (term sibling of `yearly`)
  // sku_name_map "Large Meeting — 1,000 participants (free trial)", in planName's "(Free Trial)" casing.
  // The corpus confirms the parts (Land Development, BCN, Healthcare Accreditation, Roi Et, NIMT:
  // Plan Name "Large Meeting", Details "1,000 participants").
  PLANCODE_1514: "Large Meeting — 1,000 participants (Free Trial)",
  zkw_freetrial: "Zoom Revenue Accelerator (Free Trial)", // corpus: Thairung; sku_name_map
  pro_freetrial: "Zoom Workplace Pro (Free Trial)", // corpus: Healthcare Accreditation; sku_name_map
  zoent_freetrial: "Zoom Workplace Enterprise (Free Trial)", // sku_name_map
  cmr_free_trial: "Cloud Recording Storage (Free Trial)", // corpus: Walailak
  zccnb_freetrial: "Zoom Contact Center Essentials (Free Trial)", // corpus: FlowAccount
  "ACT-ENT-LMT_500-WBN_500-T_3-yearly": "Enterprise Active Host III Annual",
  "API not available": "Zoom Events Pay Per Attendee Annual",
  "ENT-LMT_1000-WBN_1000-RCN-ZRM-threeyears": "Enterprise Plus Offer - NH 3 Year Prepay",
  "ENT-LMT_1000-WBN_1000-RCN-ZRM-twoyears": "Enterprise Plus Offer - NH 2 Year Prepay",
  "ENT-LMT_1000-WBN_1000-RCN-ZRM-yearly": "Enterprise Plus Offer - NH 1 Year Prepay",
  "ENT-LMT_500-WBN_500-threeyears": "Enterprise Named Host Three Years Prepay",
  "ENT-LMT_500-WBN_500-twoyears": "Enterprise Named Host Two Years Prepay",
  "ENT-LMT_500-WBN_500-yearly": "Enterprise Named Host Annual",
  ai_expert_assist_three_year: "AI Expert Assist Three Year PrePay",
  ai_expert_assist_two_year: "AI Expert Assist Two Year PrePay",
  ai_expert_assist_yearly: "AI Expert Assist Annual",
  biz_zpp_three_years: "Zoom Meetings Business and Zoom Phone Pro Three Year Prepay",
  biz_zpp_two_years: "Zoom Meetings Business and Zoom Phone Pro Two Year Prepay",
  biz_zpp_yearly: "Zoom Meetings Business and Zoom Phone Pro Annual",
  business_yearly: "Zoom Workplace Business Annual",
  cmr_1TB_three_year: "Cloud Recording Three Years Prepay 1TB Monthly Usage",
  cmr_1TB_two_year: "Cloud Recording Two Years Prepay 1TB Monthly Usage",
  cmr_1TB_yearly: "Cloud Recording One Year Prepay 1TB Monthly Usage",
  cmr_200GB_three_year: "Cloud Recording Three Years Prepay 200 GB Monthly Usage",
  cmr_200GB_two_year: "Cloud Recording Two Years Prepay 200 GB Monthly Usage",
  cmr_200GB_yearly: "Cloud Recording One Year Prepay 200 GB Monthly Usage",
  cmr_30GB_three_years: "Cloud Recording Three Years Prepay 30 GB Monthly Usage",
  cmr_30GB_two_years: "Cloud Recording Two Years Prepay 30 GB Monthly Usage",
  cmr_30GB_yearly: "Cloud Recording Annual Prepay 30 GB Monthly Usage",
  cmr_5TB_three_year: "Cloud Recording Three Years Prepay 5TB Monthly Usage",
  cmr_5TB_two_year: "Cloud Recording Two Years Prepay 5TB Monthly Usage",
  cmr_5TB_yearly: "Cloud Recording One Year Prepay 5TB Monthly Usage",
  concurrent_basic_three_years: "Concurrent Meeting Basic Three Year Prepay",
  concurrent_basic_two_years: "Concurrent Meeting Basic Two Year Prepay",
  concurrent_basic_yearly: "Concurrent Meeting Basic Annual",
  concurrent_plus_three_years: "Concurrent Meeting Plus Three Year Prepay",
  concurrent_plus_two_years: "Concurrent Meeting Plus Two Year Prepay",
  concurrent_plus_yearly: "Concurrent Meeting Plus Annual",
  edu20: "Education Annual",
  ent_zpp_three_years: "Zoom Meetings Enterprise and Zoom Phone Pro Three Year Prepay",
  ent_zpp_two_years: "Zoom Meetings Enterprise and Zoom Phone Pro Two Year Prepay",
  ent_zpp_yearly: "Zoom Meetings Enterprise and Zoom Phone Pro Annual",
  large1000_yearly: "1000 Participants meeting Annual",
  large500_yearly: "500 Participants meeting Annual",
  phone_calling_aunz_one_year_metered:
    "Zoom Phone Australia/New Zealand Metered Calling Named User Annual",
  phone_calling_aunz_three_year_prepay_metered:
    "Zoom Phone Australia/New Zealand Metered Calling Named User Three Year Prepay",
  phone_calling_aunz_two_year_prepay_metered:
    "Zoom Phone Australia/New Zealand Metered Calling Named User Two Year Prepay",
  phone_calling_gs_annual_metered: "Zoom Phone Pro Global Select Metered Annual",
  phone_calling_gs_three_year_prepay_metered:
    "Zoom Phone Pro Global Select Metered Three Year Prepay",
  phone_calling_gs_two_year_prepay_metered: "Zoom Phone Pro Global Select Metered Two Year Prepay",
  phone_calling_international_one_year:
    "Zoom Phone Unlimited International Calling Plan Add-On Annual",
  phone_calling_international_three_year:
    "Zoom Phone Unlimited International Calling Plan Add-On 3 Year Prepay",
  phone_calling_international_two_year:
    "Zoom Phone Unlimited International Calling Plan Add-On 2 Year Prepay",
  "phone_calling_jp_one_year_unlimited'": "Zoom Phone Japan Unlimited Calling Named User Annual",
  phone_calling_pro_global_one_year: "Zoom Phone Pro Global Select Annual",
  phone_calling_pro_global_three_year: "Zoom Phone Pro Global Select Three Year Prepay",
  phone_calling_pro_global_two_year: "Zoom Phone Pro Global Select Two Year Prepay",
  phone_calling_pro_one_year: "Zoom Phone Pro Annual",
  phone_calling_pro_three_year: "Zoom Phone Pro Three Year Prepay",
  phone_calling_pro_two_year: "Zoom Phone Pro Two Year Prepay",
  phone_calling_three_year_prepay_unlimited:
    "Zoom Phone Japan Unlimited Calling Named User Three Year Prepay",
  phone_calling_two_year_prepay_unlimited:
    "Zoom Phone Japan Unlimited Calling Named User Two Year Prepay",
  phone_calling_ukil_one_year_metered: "Zoom Phone UK/Ireland Metered Calling Named User Annual",
  phone_calling_ukil_one_year_unlimited:
    "Zoom Phone UK/Ireland Unlimited Calling Named User Annual",
  phone_calling_ukil_three_year_prepay_metered:
    "Zoom Phone UK/Ireland Metered Calling Named User Three Year Prepay",
  phone_calling_ukil_three_year_prepay_unlimited:
    "Zoom Phone UK/Ireland Unlimited Calling Named User Three Year Prepay",
  phone_calling_ukil_two_year_prepay_metered:
    "Zoom Phone UK/Ireland Metered Calling Named User Two Year Prepay",
  phone_calling_ukil_two_year_prepay_unlimited:
    "Zoom Phone UK/Ireland Unlimited Calling Named User Two Year Prepay",
  phone_calling_usca_one_year_metered:
    "Zoom Phone Pro with Phone Number and US/Canada Regional Metered Calling Plan Annual",
  phone_calling_usca_one_year_unlimited: "Zoom Phone US/Canada Unlimited Calling Named User Annual",
  phone_calling_usca_three_year_prepay_metered:
    "Zoom Phone Pro with Phone Number and US/Canada Regional Metered Calling Plan 3 Year Prepay",
  phone_calling_usca_three_year_prepay_unlimited:
    "Zoom Phone US/Canada Unlimited Calling Named User Three Year Prepay",
  phone_calling_usca_two_year_prepay_metered:
    "Zoom Phone Pro with Phone Number and US/Canada Regional Metered Calling Plan 2 Year Prepay",
  phone_calling_usca_two_year_prepay_unlimited:
    "Zoom Phone US/Canada Unlimited Calling Named User Two Year Prepay",
  phone_number_global_one_year: "Zoom Phone Global Select Telephone Number Annual",
  phone_number_global_three_year: "Zoom Phone Global Select Telephone Number 3 Year Prepay",
  phone_number_global_two_year: "Zoom Phone Global Select Telephone Number 2 Year Prepay",
  phone_number_gs_tf_one_year: "Zoom Phone Global Toll-Free Phone Numbers Annual",
  phone_number_gs_tf_three_year_prepay:
    "Zoom Phone Global Toll-Free Phone Numbers Three Year Prepay",
  phone_number_gs_tf_two_year_prepay: "Zoom Phone Global Toll-Free Phone Numbers Two Year Prepay",
  phone_number_jp_one_year: "Zoom Phone Japan Metered Calling Named User Annual",
  phone_number_jp_three_year_prepay:
    "Zoom Phone Japan Metered Calling Named User Three Year Prepay",
  phone_number_jp_two_year_prepay: "Zoom Phone Japan Metered Calling Named User Two Year Prepay",
  phone_number_premium_one_year: "Zoom Phone Premium Number Annual",
  phone_number_premium_three_year_prepay: "Zoom Phone Premium Number Three Year Prepay",
  phone_number_premium_two_year_prepay: "Zoom Phone Premium Number Two Year Prepay",
  phone_number_ukil_one_year: "Zoom Phone UK/Ireland Telephone Number Annual",
  phone_number_ukil_three_year_prepay: "Zoom Phone UK/Ireland Telephone Number 3 Year Prepay",
  phone_number_ukil_two_year_prepay: "Zoom Phone UK/Ireland Telephone Number 2 Year Prepay",
  phone_number_usca_one_year: "Zoom Phone US/Canada Telephone Number Annual",
  phone_number_usca_three_year_prepay: "Zoom Phone US/Canada Telephone Number 3 Year Prepay",
  phone_number_usca_two_year_prepay: "Zoom Phone US/Canada Telephone Number 2 Year Prepay",
  phone_payongo: "Zoom Phone Pay As You Go",
  phone_toll_free_number_nz_one_year: "Zoom Phone New Zealand Toll-Free Phone Numbers Annual",
  phone_toll_free_number_nz_three_year_prepay:
    "Zoom Phone New Zealand Toll-Free Phone Numbers Three Year Prepay",
  phone_toll_free_number_nz_two_year_prepay:
    "Zoom Phone New Zealand Toll-Free Phone Numbers Two Year Prepay",
  phone_toll_free_number_ukil_one_year: "Zoom Phone UK/Ireland Toll Free Number Annual",
  phone_toll_free_number_ukil_three_year_prepay:
    "Zoom Phone UK/Ireland Toll Free Number 3 Year Prepay",
  phone_toll_free_number_ukil_two_year_prepay:
    "Zoom Phone UK/Ireland Toll Free Number 2 Year Prepay",
  phone_toll_free_number_usca_one_year: "Zoom Phone US/Canada Toll Free Number Annual",
  phone_toll_free_number_usca_three_year_prepay:
    "Zoom Phone US/Canada Toll Free Number 3 Year Prepay",
  phone_toll_free_number_usca_two_year_prepay:
    "Zoom Phone US/Canada Toll Free Number 2 Year Prepay",
  plan_business_three_years: "Zoom Workplace Business Three Years Prepay",
  plan_business_two_years: "Zoom Workplace Business Two Years Prepay",
  plan_edu_three_years: "Education Three Years Prepay",
  plan_edu_two_years: "Education Two Years Prepay",
  plan_large1000_three_years: "1000 Participants meeting Three Years Prepay",
  plan_large1000_two_years: "1000 Participants meeting Two Years Prepay",
  plan_large500_three_years: "500 Participants meeting Three Years Prepay",
  plan_large500_two_years: "500 Participants meeting Two Years Prepay",
  plan_pro_three_years: "Zoom Workplace Pro Three Year Prepay",
  plan_pro_two_years: "Zoom Workplace Pro Two Year Prepay",
  plan_rc_three_years: "Room Connector Three Years Prepay",
  plan_rc_two_years: "Room Connector Two Years Prepay",
  plan_webinar10000_three_years: "Webinar 10000 Three Years Prepay",
  plan_webinar10000_two_years: "Webinar 10000 Two Years Prepay",
  plan_webinar1000_three_years: "Webinar 1000 Three Years Prepay",
  plan_webinar1000_two_years: "Webinar 1000 Two Years Prepay",
  plan_webinar3000_three_years: "Webinar 3000 Three Years Prepay",
  plan_webinar3000_two_years: "Webinar 3000 Two Years Prepay",
  plan_webinar5000_three_years: "Webinar 5000 Three Years Prepay",
  plan_webinar5000_two_years: "Webinar 5000 Two Years Prepay",
  plan_webinar500_three_years: "Webinar 500 Three Years Prepay",
  plan_webinar500_two_years: "Webinar 500 Two Years Prepay",
  plan_zoomroom_three_years: "Zoom Rooms Three Years Prepay",
  plan_zoomroom_two_years: "Zoom Rooms Two Years Prepay",
  pro_zpp_three_years: "Zoom Meetings Pro and Zoom Phone Pro Three Year Prepay",
  pro_zpp_two_years: "Zoom Meetings Pro and Zoom Phone Pro Two Year Prepay",
  pro_zpp_yearly: "Zoom Meetings Pro and Zoom Phone Pro Annual",
  roomconnector_yearly: "Room Connector Annual",
  siptrunk_three_yearly: "SIP Connected Audio Named Host Three Year Prepay",
  siptrunk_two_yearly: "SIP Connected Audio Named Host Two Year Prepay",
  siptrunk_yearly: "SIP Connected Audio Named Host Annual",
  tollfree_phonenumber_ukie_three_year: "Zoom UK/Ireland Toll-Free Phone Number Three Year Prepay",
  tollfree_phonenumber_ukie_two_year: "Zoom UK/Ireland Toll-Free Phone Number Two Year Prepay",
  tollfree_phonenumber_ukie_yearly: "Zoom UK/Ireland Toll-Free Phone Number Annual",
  tollfree_phonenumber_usca_three_year: "Zoom US/Canada Toll-Free Phone Number Three Year Prepay",
  tollfree_phonenumber_usca_two_year: "Zoom US/Canada Toll-Free Phone Number Two Year Prepay",
  tollfree_phonenumber_usca_yearly: "Zoom US/Canada Toll-Free Phone Number Annual",
  vspn_ukie_three_year_prepay: "Zoom UK/Ireland Virtual Service Phone Number Three Year Prepay",
  vspn_ukie_two_year_prepay: "Zoom UK/Ireland Virtual Service Phone Number Two Year Prepay",
  vspn_ukie_yearly: "Zoom UK/Ireland Virtual Service Phone Number Annual",
  vspn_usca_three_year_prepay: "Zoom US/Canada Virtual Service Phone Number Three Year Prepay",
  vspn_usca_two_year_prepay: "Zoom US/Canada Virtual Service Phone Number Two Year Prepay",
  vspn_usca_yearly: "Zoom US/Canada Virtual Service Phone Number Annual",
  webinar10000_yearly: "Webinar 10000 Annual",
  webinar1000_yearly: "Webinar 1000 Annual",
  webinar3000_yearly: "Webinar 3000 Annual",
  webinar5000_yearly: "Webinar 5000 Annual",
  webinar500_yearly: "Webinar 500 Annual",
  workspace_reservation_annual: "Workspace Reservation Annual",
  workspace_reservation_three_years: "Workspace Reservation Three Years Prepay",
  workspace_reservation_two_years: "Workspace Reservation Two Years Prepay",
  yearly: "Zoom Workplace Pro Annual",
  zcc_cs_1tb_three_year_prepay:
    "Zoom Contact Center Cloud Storage Three Years Prepay 1TB Monthly Usage",
  zcc_cs_1tb_two_year_prepay:
    "Zoom Contact Center Cloud Storage Two Years Prepay 1TB Monthly Usage",
  zcc_cs_1tb_yearly_prepay: "Zoom Contact Center Cloud Storage Annual Prepay 1TB Monthly Usage",
  zcc_cs_200gb_three_year_prepay:
    "Zoom Contact Center Cloud Storage Three Years Prepay 200GB Monthly Usage",
  zcc_cs_200gb_two_year_prepay:
    "Zoom Contact Center Cloud Storage Two Years Prepay 200GB Monthly Usage",
  zcc_cs_200gb_yearly_prepay: "Zoom Contact Center Cloud Storage Annual Prepay 200GB Monthly Usage",
  zcc_cs_30gb_three_year_prepay:
    "Zoom Contact Center Cloud Storage Three Years Prepay 30GB Monthly Usage",
  zcc_cs_30gb_two_year_prepay:
    "Zoom Contact Center Cloud Storage Two Years Prepay 30GB Monthly Usage",
  zcc_cs_30gb_yearly_prepay: "Zoom Contact Center Cloud Storage Annual Prepay 30GB Monthly Usage",
  zcc_cs_5tb_three_year_prepay:
    "Zoom Contact Center Cloud Storage Three Years Prepay 5TB Monthly Usage",
  zcc_cs_5tb_two_year_prepay:
    "Zoom Contact Center Cloud Storage Two Years Prepay 5TB Monthly Usage",
  zcc_cs_5tb_yearly_prepay: "Zoom Contact Center Cloud Storage Annual Prepay 5TB Monthly Usage",
  zcc_monthly_usage_prepay_three_year: "Zoom Contact Center Three Year Prepay Monthly Usage",
  zcc_monthly_usage_prepay_two_year: "Zoom Contact Center Two Year Prepay Monthly Usage",
  zcc_monthly_usage_prepay_yearly: "Zoom Contact Center Annual Prepay Monthly Usage",
  zcc_pay_as_you_go: "Zoom Contact Center Pay as you Go",
  zccp_three_year: "Zoom Contact Center Essentials Three Year Prepay",
  zccp_two_year: "Zoom Contact Center Essentials Two Year Prepay",
  zccp_yearly: "Zoom Contact Center Essentials Annual",
  ze_10000_unlimited_three_year_prepay: "Zoom Events 10,000 Unlimited Three Years Prepay",
  ze_10000_unlimited_two_year_prepay: "Zoom Events 10,000 Unlimited Two Years Prepay",
  ze_10000_unlimited_yearly: "Zoom Events 10,000 Unlimited Annual",
  ze_1000_unlimited_three_year_prepay: "Zoom Events 1,000 Unlimited Three Years Prepay",
  ze_1000_unlimited_two_year_prepay: "Zoom Events 1,000 Unlimited Two Years Prepay",
  ze_1000_unlimited_yearly: "Zoom Events 1,000 Unlimited Annual",
  ze_100_unlimited_three_year_prepay: "Zoom Events 100 Unlimited Three Years Prepay",
  ze_100_unlimited_two_year_prepay: "Zoom Events 100 Unlimited Two Years Prepay",
  ze_100_unlimited_yearly: "Zoom Events 100 Unlimited Annual",
  ze_20000_unlimited_three_year_prepay: "Zoom Events 20,000 Unlimited Three Years Prepay",
  ze_20000_unlimited_two_year_prepay: "Zoom Events 20,000 Unlimited Two Years Prepay",
  ze_20000_unlimited_yearly: "Zoom Events 20,000 Unlimited Annual",
  ze_30000_unlimited_three_year_prepay: "Zoom Events 30,000 Unlimited Three Years Prepay",
  ze_30000_unlimited_two_year_prepay: "Zoom Events 30,000 Unlimited Two Years Prepay",
  ze_30000_unlimited_yearly: "Zoom Events 30,000 Unlimited Annual",
  ze_3000_unlimited_three_year_prepay: "Zoom Events 3,000 Unlimited Three Years Prepay",
  ze_3000_unlimited_two_year_prepay: "Zoom Events 3,000 Unlimited Two Years Prepay",
  ze_3000_unlimited_yearly: "Zoom Events 3,000 Unlimited Annual",
  ze_50000_unlimited_three_year_prepay: "Zoom Events 50,000 Unlimited Three Years Prepay",
  ze_50000_unlimited_two_year_prepay: "Zoom Events 50,000 Unlimited Two Years Prepay",
  ze_50000_unlimited_yearly: "Zoom Events 50,000 Unlimited Annual",
  ze_5000_unlimited_three_year_prepay: "Zoom Events 5,000 Unlimited Three Years Prepay",
  ze_5000_unlimited_two_year_prepay: "Zoom Events 5,000 Unlimited Two Years Prepay",
  ze_5000_unlimited_yearly: "Zoom Events 5,000 Unlimited Annual",
  ze_500_unlimited_three_year_prepay: "Zoom Events 500 Unlimited Three Years Prepay",
  ze_500_unlimited_two_year_prepay: "Zoom Events 500 Unlimited Two Years Prepay",
  ze_ace_three_year: "Additional Co-Editor for Zoom Events Three Years Prepay",
  ze_ace_two_year: "Additional Co-Editor for Zoom Events Two Years Prepay",
  ze_ace_yearly: "Additional Co-Editor for Zoom Events Annual",
  ze_ah_three_year: "Additional Host for Zoom Events Three Years Prepay",
  ze_ah_two_year: "Additional Host for Zoom Events Two Years Prepay",
  ze_ah_yearly: "Additional Host for Zoom Events Annual",
  ze_ppa_1000_yearly: "Zoom Events Pay Per Attendee Annual",
  ze_ppa_3000_yearly: "Zoom Events Pay Per Attendee Annual",
  ze_ppa_5000_yearly: "Zoom Events Pay Per Attendee Annual",
  ze_ppa_600_yearly: "Zoom Events Pay Per Attendee Annual",
  ze_ppa_monthly_overage: "Zoom Events Pay Per Attendee Overage",
  ziq_sales_three_year: "Zoom Revenue Accelerator Three Years PrePay",
  ziq_sales_two_year: "Zoom Revenue Accelerator Two Years PrePay",
  ziq_sales_yearly: "Zoom Revenue Accelerator Annual",
  zm_contact_center_elite_three_year: "Zoom Contact Center Elite Three Year PrePay",
  zm_contact_center_elite_two_year: "Zoom Contact Center Elite Two Year PrePay",
  zm_contact_center_elite_yearly: "Zoom Contact Center Elite Annual",
  zm_contact_center_premium_three_year: "Zoom Contact Center Premium Three Year PrePay",
  zm_contact_center_premium_two_year: "Zoom Contact Center Premium Two Year PrePay",
  zm_contact_center_premium_yearly: "Zoom Contact Center Premium Annual",
  zm_hybrid_three_year: "Zoom Meeting Hybrid Three Years PrePay",
  zm_hybrid_two_year: "Zoom Meeting Hybrid Two Years PrePay",
  zm_hybrid_yearly: "Zoom Meeting Hybrid Annual",
  zm_one_ent_sta_act_yearly: "Zoom Workplace Enterprise Essentials Active Host Annual",
  zm_one_ent_std_three_year: "Zoom Workplace Enterprise Essentials Three Years PrePay",
  zm_one_ent_std_two_year: "Zoom Workplace Enterprise Essentials Two Years PrePay",
  zm_one_ent_std_yearly: "Zoom Workplace Enterprise Essentials Annual",
  zm_wf_mgt_three_year: "Zoom Workforce Management Three Years PrePay",
  zm_wf_mgt_two_year: "Zoom Workforce Management Two Years PrePay",
  zm_wf_mgt_yearly: "Zoom Workforce Management Annual",
  zmc_three_year: "Zoom Meetings Connector Three Years",
  zmc_two_year: "Zoom Meetings Connector Two Years",
  zmc_yearly: "Zoom Meetings Connector Annual",
  zmd_three_years: "Zoom Node Three Years Prepay",
  zmd_two_years: "Zoom Node Two Years Prepay",
  zmd_yearly: "Zoom Node Annual",
  zmesh_three_year_prepay: "Zoom Mesh for Events and Webinar Three Years Prepay",
  zmesh_two_year_prepay: "Zoom Mesh for Events and Webinar Two Years Prepay",
  zmesh_yearly_prepay: "Zoom Mesh for Events and Webinar Annual",
  zo_edu_ent_essentials_yearly: "Zoom Workplace for Education Enterprise Essentials Annual",
  zo_edu_ent_hied_stu_yearly: "Zoom Workplace for Education Enterprise Higher Ed Student Annual",
  zo_edu_ent_plus_yearly: "Zoom Workplace for Education Enterprise Plus Annual",
  zo_edu_ent_prem_aunz_yearly:
    "Zoom Workplace for Education Enterprise Premier Australia/New Zealand Annual",
  zo_edu_ent_prem_global_yearly:
    "Zoom Workplace for Education Enterprise Premier Global Select Annual",
  zo_edu_ent_prem_jp_yearly: "Zoom Workplace for Education Enterprise Premier Japan Annual",
  zo_edu_ent_prem_ukir_yearly: "Zoom Workplace for Education Enterprise Premier UK/Ireland Annual",
  zo_edu_ent_prem_usca_yearly: "Zoom Workplace for Education Enterprise Premier US/Canada Annual",
  zo_edu_sch_cmp_plus_aunz_yearly:
    "Zoom Workplace for Education School and Campus Plus Australia/New Zealand Annual",
  zo_edu_sch_cmp_plus_global_yearly:
    "Zoom Workplace for Education School and Campus Plus Global Select Annual",
  zo_edu_sch_cmp_plus_jp_yearly: "Zoom Workplace for Education School and Campus Plus Japan Annual",
  zo_edu_sch_cmp_plus_ukir_yearly:
    "Zoom Workplace for Education School and Campus Plus UK/Ireland Annual",
  zo_edu_sch_cmp_plus_usca_yearly:
    "Zoom Workplace for Education School and Campus Plus US/Canada Annual",
  zo_edu_sch_cmp_yearly: "Zoom Workplace for Education School and Campus Annual",
  zobp_aunz_three_year_prepay_unlimited:
    "Zoom Workplace Business Plus Australia/New Zealand Three Years Prepay",
  zobp_aunz_two_year_prepay_unlimited:
    "Zoom Workplace Business Plus Australia/New Zealand Two Years Prepay",
  zobp_aunz_yearly_unlimited: "Zoom Workplace Business Plus Australia/New Zealand Annual",
  zobp_jp_three_year_prepay_unlimited: "Zoom Workplace Business Plus Japan Three Years Prepay",
  zobp_jp_two_year_prepay_unlimited: "Zoom Workplace Business Plus Japan Two Years Prepay",
  zobp_jp_yearly_unlimited: "Zoom Workplace Business Plus Japan Annual",
  zobp_pro_global_select_three_year_prepay:
    "Zoom Workplace Business Plus Global Select Three Years PrePay",
  zobp_pro_global_select_two_year_prepay:
    "Zoom Workplace Business Plus Global Select Two Years PrePay",
  zobp_pro_global_select_yearly: "Zoom Workplace Business Plus Global Select Annual",
  zobp_ukil_three_year_prepay_unlimited:
    "Zoom Workplace Business Plus UK/Ireland Three Years Prepay",
  zobp_ukil_two_year_prepay_unlimited: "Zoom Workplace Business Plus UK/Ireland Two Years Prepay",
  zobp_ukil_yearly_unlimited: "Zoom Workplace Business Plus UK/Ireland Annual",
  zobp_usca_three_year_prepay_unlimited:
    "Zoom Workplace Business Plus US/Canada Three Years Prepay",
  zobp_usca_two_year_prepay_unlimited: "Zoom Workplace Business Plus US/Canada Two Years Prepay",
  zobp_usca_yearly_unlimited: "Zoom Workplace Business Plus US/Canada Annual",
  zoe_prem_usca_three_year: "Zoom Workplace Enterprise Premier US/Canada Three Years Prepay",
  zoe_prem_usca_two_year: "Zoom Workplace Enterprise Premier US/Canada Two Years Prepay",
  zoe_prem_usca_yearly: "Zoom Workplace Enterprise Premier US/Canada Annual",
  zoe_three_year: "Zoom Workplace Enterprise Three Year Prepay",
  zoe_two_year: "Zoom Workplace Enterprise Two Year Prepay",
  zoe_yearly: "Zoom Workplace Enterprise Annual",
  zoep_three_year: "Zoom Workplace Enterprise Plus 3 Year Prepay",
  zoep_two_year: "Zoom Workplace Enterprise Plus 2 Year Prepay",
  zoep_yearly: "Zoom Workplace Enterprise Plus Annual",
  zoomaudio_unlimit_usca_three_years: "Audio License Unlimited US and Canada Three Year Prepay",
  zoomaudio_unlimit_usca_two_years: "Audio License Unlimited US and Canada Two Year Prepay",
  zoomaudio_unlimit_usca_yearly: "Audio License US and Canada Annual",
  zp_comm_three_years: "Zoom Phone Common Area 3 Year Pre Pay",
  zp_comm_two_years: "Zoom Phone Common Area 2 Year Pre Pay",
  zp_comm_yearly: "Zoom Phone Common Area Annual",
  zp_power_pack_three_years: "Zoom Phone Customer Engagement Pack Three Years PrePay",
  zp_power_pack_two_years: "Zoom Phone Customer Engagement Pack Two Years PrePay",
  zp_power_pack_yearly: "Zoom Phone Customer Engagement Pack Annual",
  zphone_hybrid_three_years: "Zoom Phone Hybrid Three Years Prepay",
  zphone_hybrid_two_years: "Zoom Phone Hybrid Two Years Prepay",
  zphone_hybrid_yearly: "Zoom Phone Hybrid Annual",
  zrc_three_year: "Zoom Recordings Connector Three Years",
  zrc_two_year: "Zoom Recordings Connector Two Years",
  zrc_yearly: "Zoom Recordings Connector Annual",
  zroom_yearly: "Zoom Rooms Annual",
  zs_10000_unlimited_three_year: "Zoom Webinars Plus 10,000 Unlimited Three Years Prepay",
  zs_10000_unlimited_two_year: "Zoom Webinars Plus 10,000 Unlimited Two Years Prepay",
  zs_10000_unlimited_yearly: "Zoom Webinars Plus 10,000 Unlimited Annual",
  zs_1000_unlimited_three_year: "Zoom Webinars Plus 1,000 Unlimited Three Years Prepay",
  zs_1000_unlimited_two_year: "Zoom Webinars Plus 1,000 Unlimited Two Years Prepay",
  zs_1000_unlimited_yearly: "Zoom Webinars Plus 1,000 Unlimited Annual",
  zs_100_unlimited_three_year: "Zoom Webinars Plus 100 Unlimited Three Years Prepay",
  zs_100_unlimited_two_year: "Zoom Webinars Plus 100 Unlimited Two Years Prepay",
  zs_100_unlimited_yearly: "Zoom Webinars Plus 100 Unlimited Annual",
  zs_20000_unlimited_three_year: "Zoom Webinars Plus 20,000 Unlimited Three Years Prepay",
  zs_20000_unlimited_two_year: "Zoom Webinars Plus 20,000 Unlimited Two Years Prepay",
  zs_20000_unlimited_yearly: "Zoom Webinars Plus 20,000 Unlimited Annual",
  zs_30000_unlimited_three_year: "Zoom Webinars Plus 30,000 Unlimited Three Years Prepay",
  zs_30000_unlimited_two_year: "Zoom Webinars Plus 30,000 Unlimited Two Years Prepay",
  zs_30000_unlimited_yearly: "Zoom Webinars Plus 30,000 Unlimited Annual",
  zs_3000_unlimited_three_year: "Zoom Webinars Plus 3,000 Unlimited Three Years Prepay",
  zs_3000_unlimited_two_year: "Zoom Webinars Plus 3,000 Unlimited Two Years Prepay",
  zs_3000_unlimited_yearly: "Zoom Webinars Plus 3,000 Unlimited Annual",
  zs_50000_unlimited_three_year: "Zoom Webinars Plus 50,000 Unlimited Three Years Prepay",
  zs_50000_unlimited_two_year: "Zoom Webinars Plus 50,000 Unlimited Two Years Prepay",
  zs_50000_unlimited_yearly: "Zoom Webinars Plus 50,000 Unlimited Annual",
  zs_5000_unlimited_three_year: "Zoom Webinars Plus 5,000 Unlimited Three Years Prepay",
  zs_5000_unlimited_two_year: "Zoom Webinars Plus 5,000 Unlimited Two Years Prepay",
  zs_5000_unlimited_yearly: "Zoom Webinars Plus 5,000 Unlimited Annual",
  zs_500_unlimited_three_year: "Zoom Webinars Plus 500 Unlimited Three Years Prepay",
  zs_500_unlimited_two_year: "Zoom Webinars Plus 500 Unlimited Two Years Prepay",
  zs_500_unlimited_yearly: "Zoom Webinars Plus 500 Unlimited Annual",
  zs_ace_three_year: "Additional Co-Editor for Zoom Webinars Plus Three Years Prepay",
  zs_ace_two_year: "Additional Co-Editor for Zoom Webinars Plus Two Years Prepay",
  zs_ace_yearly: "Additional Co-Editor for Zoom Webinars Plus Annual",
  zs_ah_three_year: "Additional Host for Zoom Webinars Plus Three Years Prepay",
  zs_ah_two_year: "Additional Host for Zoom Webinars Plus Two Years Prepay",
  zs_ah_yearly: "Additional Host for Zoom Webinars Plus Annual",
  zs_ppa_1000_yearly: "Zoom Webinars Plus Pay Per Attendee Annual",
  zs_ppa_3000_yearly: "Zoom Webinars Plus Pay Per Attendee Annual",
  zs_ppa_5000_yearly: "Zoom Webinars Plus Pay Per Attendee Annual",
  zs_ppa_600_yearly: "Zoom Webinars Plus Pay Per Attendee Annual",
  zs_ppa_monthly_overage: "Zoom Webinars Plus Pay Per Attendee Overage",
  zsched_three_year: "Zoom Scheduler Three Years PrePay",
  zsched_two_year: "Zoom Scheduler Two Years PrePay",
  zsched_yearly: "Zoom Scheduler Annual",
  ztransl_three_years: "Zoom Translated Captions Three Years Prepay",
  ztransl_two_years: "Zoom Translated Captions Two Years Prepay",
  ztransl_yearly: "Zoom Translated Captions Annual",
  zva_three_year: "Zoom Virtual Agent Three Years Prepay Monthly Usage",
  zva_two_year: "Zoom Virtual Agent Two Years Prepay Monthly Usage",
  zva_yearly: "Zoom Virtual Agent Annual Prepay Monthly Usage",
  zwb_plus_three_year: "Zoom Whiteboard Plus - Unlimited Boards Three Years Prepay",
  zwb_plus_two_year: "Zoom Whiteboard Plus - Unlimited Boards Two Years Prepay",
  zwb_plus_yearly: "Zoom Whiteboard Plus - Unlimited Boards Annual",
  zwb_three_year: "Zoom Whiteboard - Unlimited Boards - 3 Year Prepay",
  zwb_two_year: "Zoom Whiteboard - Unlimited Boards - 2 Year Prepay",
  zwb_yearly: "Zoom Whiteboard - Unlimited Boards - 1 Year Prepay",
};

/** Period suffixes on an API plan type, longest-first so "_free_trial" wins over "_trial". */
const PERIODS: ReadonlyArray<[RegExp, string]> = [
  [/_(free_?trial)$/i, "Free Trial"],
  [/_(promo_annual)$/i, "Annual"],
  [/_(three_years?)$/i, "Three Year"],
  [/_(two_years?)$/i, "Two Year"],
  [/_(one_month|monthly)$/i, "Monthly"],
  [/_(annual|yearly)$/i, "Annual"],
];

/**
 * Bare period tokens that appear as a WHOLE base plan_type (no leading "_"), e.g. "yearly", "monthly",
 * "trial". The base plan carries these (1204 accounts are literally "yearly") — matched anchored so they
 * never fire mid-string (only PERIODS, which require a leading "_", strips suffixes inside planName()).
 */
const BARE_PERIODS: ReadonlyArray<[RegExp, string]> = [
  [/^(free_?trial|trial)$/i, "Free Trial"],
  [/^(three_years?)$/i, "Three Year"],
  [/^(two_years?)$/i, "Two Year"],
  [/^(one_month|monthly)$/i, "Monthly"],
  [/^(annual|yearly)$/i, "Annual"],
];

function titleize(s: string): string {
  return s
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Resolve an opaque Zoom API plan type to a human plan name. Exact price-list match first; otherwise
 * strip the billing period and map the base product (so "business_freetrial" -> "Zoom Workplace Business
 * (Free Trial)"); finally fall back to a title-cased version of the raw type. Empty -> em dash.
 */
export function planName(apiType: string | null | undefined): string {
  if (!apiType) return "—";
  const exact = PLAN_NAMES[apiType];
  if (exact) return exact;

  let base = apiType;
  let period = "";
  for (const [re, label] of PERIODS) {
    if (re.test(apiType)) {
      base = apiType.replace(re, "");
      period = label;
      break;
    }
  }
  for (const suffix of ["_yearly", "_annual"]) {
    const hit = PLAN_NAMES[base + suffix];
    if (hit) {
      const product = hit.replace(/\s*(Annual|Yearly)$/i, "").trim();
      return period ? `${product} (${period})` : product;
    }
  }
  const pretty = titleize(base) || apiType;
  return period ? `${pretty} (${period})` : pretty;
}

/**
 * Period words scanned in the RESOLVED plan NAME as a last resort — for opaque base codes whose type
 * carries no period token but whose human name does (e.g. `edu20` → "Education Annual" → Annual, the
 * count Zoom itself shows). Longest/most-specific first so "Free Trial" wins over "Annual".
 */
const NAME_PERIODS: ReadonlyArray<[RegExp, string]> = [
  // COMMITMENT period is matched BEFORE the metering unit: a name like "…Annual Prepay …GB Monthly
  // Usage" (the zcc cloud-storage SKUs) is ANNUAL — the yearly commitment — NOT "Monthly" (the usage
  // meter). All word-bounded so a period word never fires mid-token ("…yearlyish" → no match).
  [/free\s*trial/i, "Free Trial"],
  [/\bthree[\s-]*years?\b/i, "Three Year"],
  [/\btwo[\s-]*years?\b/i, "Two Year"],
  [/\b(annual|yearly)\b/i, "Annual"],
  [/\bmonth(ly)?\b/i, "Monthly"],
];

/**
 * Billing period label derived from the API plan type. Handles suffixed types ("zoe_yearly" → Annual)
 * via PERIODS, the BARE base tokens ("yearly"/"monthly"/"trial") via BARE_PERIODS, and finally opaque
 * base codes (e.g. "edu20") by scanning their RESOLVED name ("Education Annual" → Annual). Empty / a
 * name with no period signal (e.g. "Zoom Phone Pay As You Go") → em dash.
 */
export function billingPeriod(apiType: string | null | undefined): string {
  if (!apiType) return "—";
  for (const [re, label] of PERIODS) if (re.test(apiType)) return label;
  for (const [re, label] of BARE_PERIODS) if (re.test(apiType)) return label;
  const name = planName(apiType);
  for (const [re, label] of NAME_PERIODS) if (re.test(name)) return label;
  return "—";
}

/**
 * Bucket a Zoom `base_type` plan key into the FOUR broad tiers the Pool module's Account Type column
 * actually wants (Pro / Business / Enterprise / Education) — not one of this file's 300+ granular SKUs.
 *
 * rev 10 (22/7, principal decision): PoolAccountsPage's Account Type column was believed (rev 7, 21/7) to
 * receive a clean label ("Pro" / "Education" / …) straight from the BE. Verified FALSE on prod 21/7 — it
 * carries the same raw base_type-style keys as every other screen (edu20 / yearly / zoe_yearly / …), so
 * this is a genuinely new, coarser mapping that column needs — not a case for reusing planName().
 *
 * Bucketed by KEY FAMILY (prefix/substring on the raw key, case-insensitive), most-specific rule first so
 * e.g. "zoep_yearly" (Enterprise Plus) hits the zoe* rule before it could ever reach the Pro fallthrough:
 *   - edu*                                 -> "Education"
 *   - zoe* / zwe* / *enterprise*            -> "Enterprise"
 *   - *business* / zbus*                    -> "Business"
 *   - bare "yearly" / "monthly", pro*, zp*  -> "Pro"
 * Anything else falls back to planName() — a readable full name, NEVER the raw key, so an unbucketed SKU
 * still reads as English rather than an opaque code. Verified against the real prod keys the principal
 * named 21/7: edu20 -> Education, yearly -> Pro, zoe_yearly -> Enterprise (Dextra/PAC — principal
 * confirmed Enterprise is correct for that account).
 *
 * null -> null: em dash is the CALLER's job, same split as planName() (which owns its "—" itself instead —
 * the two differ here because the Pool Accounts cell already has its own null-cell convention to match).
 */
export function tierOf(baseType: string | null): string | null {
  if (!baseType) return null;
  const key = baseType.toLowerCase();
  // ครอบทุก family ของ base plan ให้สมมาตรทุกอายุสัญญา (r13 lens: 2-year Business เข้า bucket
  // แต่ 2-year Pro/Edu หลุดไปโชว์ชื่อยาว) — zo_edu_* คือ Education ยุคใหม่, zobp_* คือ Business
  // Plus (อยู่ bucket Business ตามกรอบ 4 ระดับของคุณใหม่) · ห้ามใส่ ^zp (นั่น Zoom PHONE ไม่ใช่
  // Pro — เคย map ผิดแล้ว test ยังตอกย้ำความผิดไว้ด้วย)
  if (/^edu|^plan_edu|^zo_edu/.test(key)) return "Education";
  if (/^zoe/.test(key)) return "Enterprise";
  if (/business|^zobp/.test(key)) return "Business";
  if (/^(yearly|monthly)$/.test(key) || /^pro|^plan_pro/.test(key)) return "Pro";
  return planName(baseType);
}
